import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Topbar from '../components/Topbar';
import api from '../api';

const COLORS = ['#066ac9','#0cbc87','#f7c32e','#d6293e','#6f42c1','#fd7e14','#17a2b8'];

function initiales(prenom, nom) {
  return `${(prenom||'?')[0]}${(nom||'?')[0]}`.toUpperCase();
}

export default function FormulaireResultats() {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [eleves, setEleves] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classeFiltre, setClasseFiltre] = useState('');
  const [qrVisible, setQrVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/formulaires/${id}`),
      api.get(`/formulaires/${id}/reponses`),
    ]).then(([f, r]) => {
      setForm(f.data);
      setEleves(r.data);
      const cls = [...new Set(r.data.map(e => e.classe_nom).filter(Boolean))].sort();
      setClasses(cls);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  const filteredEleves = classeFiltre
    ? eleves.filter(e => e.classe_nom === classeFiltre)
    : eleves;

  const url = `${window.location.origin}/s/${form?.code_acces}`;

  if (loading) return (
    <div className="d-flex align-items-center justify-content-center" style={{ height: '80vh' }}>
      <div className="spinner-border" style={{ color: 'var(--primary)' }}></div>
    </div>
  );
  if (!form) return <div className="page-content"><div className="alert alert-danger">Formulaire introuvable</div></div>;

  return (
    <>
      <Topbar
        title={form.titre}
        actions={
          <>
            <button className="btn btn-light" onClick={() => setQrVisible(true)}>
              <i className="bi bi-qr-code me-2"></i>QR Code
            </button>
            <Link to={`/formulaire/${id}/modifier`} className="btn btn-light">
              <i className="bi bi-pencil me-2"></i>Modifier
            </Link>
          </>
        }
      />
      <div className="page-content">

        {/* Bannière du formulaire */}
        <div className="card mb-4" style={{ background: 'linear-gradient(135deg, #066ac9, #044a8d)', border: 'none' }}>
          <div className="card-body text-white d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div>
              <h5 className="mb-1 fw-semibold">{form.titre}</h5>
              {form.description && <p className="mb-2 opacity-75" style={{ fontSize: '0.875rem' }}>{form.description}</p>}
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <span className="badge bg-white text-primary fw-semibold px-3 py-2" style={{ fontSize: '0.9rem', letterSpacing: 1 }}>
                  {form.code_acces}
                </span>
                <span className={`badge ${form.actif ? 'bg-success' : 'bg-secondary'}`}>{form.actif ? 'Actif' : 'Fermé'}</span>
                <span className="opacity-75" style={{ fontSize: '0.8rem' }}>
                  {form.nb_questions || form.questions?.length} questions · {eleves.length} réponse{eleves.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-light btn-sm" onClick={() => navigator.clipboard.writeText(url)}>
                <i className="bi bi-clipboard me-1"></i>Copier le lien
              </button>
              <a href={url} target="_blank" rel="noreferrer" className="btn btn-light btn-sm">
                <i className="bi bi-box-arrow-up-right me-1"></i>Ouvrir
              </a>
            </div>
          </div>
        </div>

        {/* Filtres */}
        {classes.length > 0 && (
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <button
              className={`btn btn-sm ${classeFiltre === '' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setClasseFiltre('')}
            >
              Toutes les classes ({eleves.length})
            </button>
            {classes.map((c, i) => {
              const n = eleves.filter(e => e.classe_nom === c).length;
              return (
                <button
                  key={c}
                  className={`btn btn-sm ${classeFiltre === c ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setClasseFiltre(c)}
                >
                  {c} ({n})
                </button>
              );
            })}
          </div>
        )}

        {/* Tableau des réponses */}
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <span className="fw-semibold">
              Réponses {classeFiltre ? `— ${classeFiltre}` : ''} ({filteredEleves.length})
            </span>
          </div>
          {filteredEleves.length === 0 ? (
            <div className="card-body text-center py-5 text-muted">
              <i className="bi bi-inbox" style={{ fontSize: '2.5rem', opacity: 0.2 }}></i>
              <p className="mt-2 mb-0">Aucune réponse reçue.</p>
              <p style={{ fontSize: '0.85rem' }}>Partagez le lien ou le QR code aux élèves.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th className="ps-3">Élève</th>
                    <th>Classe</th>
                    {form.questions?.filter(q => q.type !== 'separateur').slice(0, 4).map(q => (
                      <th key={q.id} style={{ maxWidth: 160 }}>
                        <span title={q.libelle} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 160 }}>
                          {q.libelle}
                        </span>
                      </th>
                    ))}
                    <th>Fiche</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEleves.map((e, i) => (
                    <tr key={e.id}>
                      <td className="ps-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="avatar-sm" style={{ background: COLORS[i % COLORS.length] }}>
                            {initiales(e.prenom, e.nom)}
                          </div>
                          <span className="fw-medium">{e.prenom} {e.nom}</span>
                        </div>
                      </td>
                      <td>{e.classe_nom || <span className="text-muted">—</span>}</td>
                      {form.questions?.filter(q => q.type !== 'separateur').slice(0, 4).map(q => {
                        const rep = e.reponses?.find(r => r.question_id === q.id);
                        let val = rep?.valeur || '';
                        try { val = JSON.parse(val); if (Array.isArray(val)) val = val.join(', '); } catch {}
                        return (
                          <td key={q.id} style={{ maxWidth: 160 }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: 160 }}>
                              {val || <span className="text-muted">—</span>}
                            </span>
                          </td>
                        );
                      })}
                      <td>
                        <Link to={`/eleve/${e.id}`} className="btn btn-sm btn-primary">
                          <i className="bi bi-person-lines-fill me-1"></i>Voir
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* QR Modal */}
      {qrVisible && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={() => setQrVisible(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">QR Code — {form.titre}</h5>
                <button className="btn-close" onClick={() => setQrVisible(false)}></button>
              </div>
              <div className="modal-body qr-container">
                <QRCodeSVG value={url} size={220} />
                <p className="mt-3 mb-1 fw-medium">Lien élève :</p>
                <div className="d-flex gap-2 justify-content-center align-items-center">
                  <code className="bg-light px-3 py-1 rounded" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>{url}</code>
                  <button className="btn btn-sm btn-light flex-shrink-0" onClick={() => navigator.clipboard.writeText(url)}>
                    <i className="bi bi-clipboard"></i>
                  </button>
                </div>
                <p className="mt-2 text-muted" style={{ fontSize: '0.8rem' }}>Code : <strong>{form.code_acces}</strong></p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
